import React, { useEffect, useState } from 'react'

import { useBottomScrollListener } from 'react-bottom-scroll-listener'
import { Trans, useTranslation } from 'react-i18next'

import OrderDropdown from '@components/myScores/ControlArea/OrderDropdown'
import ScoresView from '@components/myScores/ScoresView'
import { ScoreViewOptions } from '@components/myScores/utils/types'
import ExternalLink from '@components/shared/ExternalLink'
import LayoutChanger from '@components/shared/LayoutChanger'
import LoadingSpinner from '@components/shared/LoadingSpinner'
import NonIdealState from '@components/shared/NonIdealState'
import { FAVORITES_PAGE_SIZE } from '@pages/home/loaders/favorites.loader'
import { PurchasedScoresResponse } from '@pages/home/loaders/purchasedScores.loader'
import AudioPreviewProvider from '@providers/AudioPreviewProvider'
import ApiRoutes from '@utils/api-routes'
import { ViewType } from '@utils/global'
import http from '@utils/http'
import PageRoutes from '@utils/page-routes'
import Storage from '@utils/storage'

interface Props {
  initialData: PurchasedScoresResponse
  order: string | null
}

const PurchasedScoresView: React.FC<Props> = ({ initialData, order }) => {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(false)
  const [scoresData, setScoresData] = useState(initialData)
  const [layoutType, setLayoutType] = useState(Storage.scoresViewType)
  const setViewType = (type: ViewType): void => {
    setLayoutType(type)
    Storage.scoresViewType = type
  }

  const { scores, remaining, startIndex } = scoresData

  const scrollRef = useBottomScrollListener<HTMLDivElement>(
    async () => {
      if (!remaining || loading) return

      setLoading(true)
      const [newResp] = await http.post<PurchasedScoresResponse>(
        ApiRoutes.members.purchasedScoresPath(),
        {
          order,
          max_count: FAVORITES_PAGE_SIZE,
          start_index: startIndex,
        }
      )

      if (newResp?.data) {
        setScoresData((prevData) => ({
          ...newResp.data,
          scores: [...prevData.scores, ...newResp.data.scores],
        }))
      }
      setLoading(false)
    },
    {
      triggerOnNoScroll: true,
      offset: 32,
    }
  )

  // Reset the data when we get something new loaded
  useEffect(() => {
    setScoresData(initialData)
  }, [initialData])

  if (!scores.length) {
    return (
      <NonIdealState
        title={t('content.empty.purchase.title')}
        icon="bag"
        subtitle={
          <Trans i18nKey="content.empty.purchase.subtitle">
            Purchase&nbsp;
            <ExternalLink href={PageRoutes.musicPath()} className="orange-link">
              music
            </ExternalLink>
            &nbsp;to print and download.
          </Trans>
        }
      />
    )
  }

  const options: ScoreViewOptions = {
    hideState: true,
    showAuthor: true,
    readonly: true,
  }

  return (
    <>
      <div className="h-16 flex flex-row items-center justify-end -mr-1">
        <OrderDropdown order={order} />
        <LayoutChanger
          setViewType={setViewType}
          viewType={layoutType}
          types={[ViewType.GRID, ViewType.LIST]}
        />
      </div>
      <div className="flex-1 overflow-y-auto relative pb-4" ref={scrollRef}>
        <AudioPreviewProvider>
          <ScoresView scores={scores} viewType={layoutType} options={options} />
        </AudioPreviewProvider>
        {remaining && loading && <LoadingSpinner />}
      </div>
    </>
  )
}

export default PurchasedScoresView
